import { useMemo } from 'react';

import type { NoteGenerationErrorProps } from '@repo/videa-notes';

import type { ClinicalNote } from '@/react-query/videa-notes/useExistingNotes';
import type { PromptOption } from '@/react-query/videa-notes/usePrompts';

import type {
  VideaNotesCoreContextValue,
  VideaNotesNoteResultContextValue,
  VideaNotesPatient,
  VideaNotesRecordingContextValue,
} from '../context';
import type {
  VideaNotesUIState,
  VoiceNoteTemplateOption,
  VoiceNotesSource,
} from '../types';

// ============================================================================
// Core Context Value Hook
// ============================================================================

interface UseCoreContextValueParams {
  uiStatus: VideaNotesUIState;
  voiceNotesSource: VoiceNotesSource;
  patientName: string;
  patient: VideaNotesPatient | undefined;
  standalonePatientVisitId: string | undefined;
  isVoiceNotesConsentEnabled: boolean;
  isVoiceNotesCustomTemplatesEnabled: boolean;
  processingError: NoteGenerationErrorProps | undefined;
  onCloseSheet: () => void;
  onPatientCreated: (visitData: {
    id: string;
    firstName: string;
    lastName: string | null;
    sourcePatientId: string | null;
    dateOfBirth: Date | null;
    appointmentDate: Date;
    appointmentStartTime: string;
  }) => void;
}

export const useCoreContextValue = ({
  uiStatus,
  voiceNotesSource,
  patientName,
  patient,
  standalonePatientVisitId,
  isVoiceNotesConsentEnabled,
  isVoiceNotesCustomTemplatesEnabled,
  processingError,
  onCloseSheet,
  onPatientCreated,
}: UseCoreContextValueParams): VideaNotesCoreContextValue => {
  return useMemo(
    () => ({
      uiStatus,
      voiceNotesSource,
      patientName,
      patient,
      standalonePatientVisitId,
      isVoiceNotesConsentEnabled,
      isVoiceNotesCustomTemplatesEnabled,
      processingError,
      onCloseSheet,
      onPatientCreated,
    }),
    [
      uiStatus,
      voiceNotesSource,
      patientName,
      patient,
      standalonePatientVisitId,
      isVoiceNotesConsentEnabled,
      isVoiceNotesCustomTemplatesEnabled,
      processingError,
      onCloseSheet,
      onPatientCreated,
    ],
  );
};

// ============================================================================
// Recording Context Value Hook
// ============================================================================

interface UseRecordingContextValueParams {
  recorderStatus: string;
  mediaBlobUrl: string | undefined;
  promptOptions: PromptOption[];
  selectedPrompt: string | undefined;
  templateOptions: VoiceNoteTemplateOption[];
  selectedTemplate: string | undefined;
  isAudioUploadEnabled: boolean;
  showDevTools: boolean;
  onSelectedPrompt: (prompt: string | undefined) => void;
  onSelectedTemplate: (templateId: string | undefined) => void;
  onMonitoringStreamChange: (stream: MediaStream | null) => void;
  onCancelRecording: () => void;
  onCancelRecordingConfirmation: () => void;
  onPauseRecording: () => void;
  onResumeRecording: () => void;
  onStartRecording: () => void;
  onStartProviderOnlyRecording: () => void;
  onGenerateNote: (uploadedFile?: File, blobUrl?: string) => void;
  onPatientConsentDenied: () => void;
}

export const useRecordingContextValue = ({
  recorderStatus,
  mediaBlobUrl,
  promptOptions,
  selectedPrompt,
  templateOptions,
  selectedTemplate,
  isAudioUploadEnabled,
  showDevTools,
  onSelectedPrompt,
  onSelectedTemplate,
  onMonitoringStreamChange,
  onCancelRecording,
  onCancelRecordingConfirmation,
  onPauseRecording,
  onResumeRecording,
  onStartRecording,
  onStartProviderOnlyRecording,
  onGenerateNote,
  onPatientConsentDenied,
}: UseRecordingContextValueParams): VideaNotesRecordingContextValue => {
  return useMemo(
    () => ({
      recorderStatus,
      mediaBlobUrl,
      promptOptions,
      selectedPrompt,
      templateOptions,
      selectedTemplate,
      isAudioUploadEnabled,
      showDevTools,
      onSelectedPrompt,
      onSelectedTemplate,
      onMonitoringStreamChange,
      onCancelRecording,
      onCancelRecordingConfirmation,
      onPauseRecording,
      onResumeRecording,
      onStartRecording,
      onStartProviderOnlyRecording,
      onGenerateNote,
      onPatientConsentDenied,
    }),
    [
      recorderStatus,
      mediaBlobUrl,
      promptOptions,
      selectedPrompt,
      templateOptions,
      selectedTemplate,
      isAudioUploadEnabled,
      showDevTools,
      onSelectedPrompt,
      onSelectedTemplate,
      onMonitoringStreamChange,
      onCancelRecording,
      onCancelRecordingConfirmation,
      onPauseRecording,
      onResumeRecording,
      onStartRecording,
      onStartProviderOnlyRecording,
      onGenerateNote,
      onPatientConsentDenied,
    ],
  );
};

// ============================================================================
// Note Result Context Value Hook
// ============================================================================

interface UseNoteResultContextValueParams {
  clinicalNotesData: ClinicalNote[];
  selectedNote: ClinicalNote | undefined;
  noteContent: string;
  patientConsentBlurb: string | undefined;
  isNoteReadOnly: boolean;
  isNoteSavedToPMS: boolean;
  isNoteBeingSavedToPMS: boolean;
  isSavingNoteUpdate: boolean;
  isSavingNoteToPMS: boolean;
  isRetryingWriteback: boolean;
  hasWritebackEnabled: boolean;
  showCopyTooltip: boolean;
  onNoteChange: (value: string) => void;
  onNoteClick: () => void;
  onSelectedNote: (noteId: string | undefined) => void;
  onCopyText: (text: string) => void;
  onCreateAdditionalNote: () => void;
  onSaveNoteToPMS: () => void;
  onRetryWritebackToPMS: () => void;
}

export const useNoteResultContextValue = ({
  clinicalNotesData,
  selectedNote,
  noteContent,
  patientConsentBlurb,
  isNoteReadOnly,
  isNoteSavedToPMS,
  isNoteBeingSavedToPMS,
  isSavingNoteUpdate,
  isSavingNoteToPMS,
  isRetryingWriteback,
  hasWritebackEnabled,
  showCopyTooltip,
  onNoteChange,
  onNoteClick,
  onSelectedNote,
  onCopyText,
  onCreateAdditionalNote,
  onSaveNoteToPMS,
  onRetryWritebackToPMS,
}: UseNoteResultContextValueParams): VideaNotesNoteResultContextValue => {
  return useMemo(
    () => ({
      clinicalNotesData,
      selectedNote,
      noteContent,
      patientConsentBlurb,
      isNoteReadOnly,
      isNoteSavedToPMS,
      isNoteBeingSavedToPMS,
      isSavingNoteUpdate,
      isSavingNoteToPMS,
      isRetryingWriteback,
      hasWritebackEnabled,
      showCopyTooltip,
      onNoteChange,
      onNoteClick,
      onSelectedNote,
      onCopyText,
      onCreateAdditionalNote,
      onSaveNoteToPMS,
      onRetryWritebackToPMS,
    }),
    [
      clinicalNotesData,
      selectedNote,
      noteContent,
      patientConsentBlurb,
      isNoteReadOnly,
      isNoteSavedToPMS,
      isNoteBeingSavedToPMS,
      isSavingNoteUpdate,
      isSavingNoteToPMS,
      isRetryingWriteback,
      hasWritebackEnabled,
      showCopyTooltip,
      onNoteChange,
      onNoteClick,
      onSelectedNote,
      onCopyText,
      onCreateAdditionalNote,
      onSaveNoteToPMS,
      onRetryWritebackToPMS,
    ],
  );
};
